/**
 * Tela /custom-arts — Biblioteca de artes (TH-55166).
 *
 * Galeria em modo abas com 4 tabs mapeando os 3 scopes do backend
 * (TH-55161) + aba "Todas":
 *   Todas       → sem filtro scope (backend faz merge dos 3)
 *   Minhas      → scope=tenant
 *   Priceshow   → scope=global
 *   Patrocinadas→ scope=supplier
 *
 * Aba selecionada persiste na query string via TanStack Router
 * (`?tab=Minhas`). Aba default "Todas" não aparece na URL.
 *
 * Filtros adicionais: busca por nome (debounced 300ms), loja, área.
 * Grid responsivo 1/2/3/4 colunas.
 * Card memoizado `CustomArtCard` com badge de origem color-coded.
 *
 * Refatorado a partir de TH-53368 (M5.E6.S2.1) para incluir abas
 * multi-scope de TH-55166 (M-artes3.7).
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ImageIcon, Plus, RefreshCw, Sparkles, Upload } from "lucide-react";

import { AppLayout } from "@/components/AppLayout";
import { CustomArtCard } from "@/components/CustomArtCard";
import { CustomArtEditDialog } from "@/components/CustomArtEditDialog";
import { CustomArtGenerateModal } from "@/components/CustomArtGenerateModal";
import { CustomArtUploadModal } from "@/components/CustomArtUploadModal";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { FilterPills } from "@/components/ui/FilterPills";
import { Input } from "@/components/ui/Input";
import { Pagination } from "@/components/ui/Pagination";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import type { CustomArtRead } from "@/hooks/useCustomArts";
import type { StatusTone } from "@/components/ui/StatusPill";
import {
  useCustomArtsList,
  type CustomArtListFilters,
  type CustomArtTab,
} from "@/hooks/useCustomArts";
import { useAuth } from "@/hooks/useAuth";

// ---------------------------------------------------------------------------
// Tipos locais
// ---------------------------------------------------------------------------

interface StoreLite {
  id: string;
  name: string;
}

interface AreaLite {
  id: string;
  name: string;
  slug: string;
}

interface PagedStores {
  data: StoreLite[];
  meta: { total: number };
}
interface PagedAreas {
  data: AreaLite[];
  meta: { total: number };
}

// Exportado para o validateSearch em router.tsx (TH-55166).
export interface CustomArtsSearch {
  tab?: CustomArtTab;
}

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------

const PER_PAGE = 24;

const TAB_OPTIONS: { label: string; value: CustomArtTab }[] = [
  { label: "Todas", value: "Todas" },
  { label: "Minhas", value: "Minhas" },
  { label: "Priceshow", value: "Priceshow" },
  { label: "Patrocinadas", value: "Patrocinadas" },
];

const TAB_EMPTY_MESSAGES: Record<CustomArtTab, string> = {
  Todas: "Nenhuma arte por aqui ainda.",
  Minhas: "Você ainda não subiu nenhuma arte.",
  Priceshow: "Nenhuma arte Priceshow disponível.",
  Patrocinadas:
    "Este tenant não possui artes de fornecedores patrocinadores.",
};

// ---------------------------------------------------------------------------
// Helpers de fetch (stores + areas para descrição de escopo)
// ---------------------------------------------------------------------------

async function fetchStoresAll(): Promise<PagedStores> {
  const res = await fetch("/api/stores?page=1&per_page=100", {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as PagedStores;
}

async function fetchAreasAll(): Promise<PagedAreas> {
  const res = await fetch("/api/areas?page=1&per_page=100", {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as PagedAreas;
}

// ---------------------------------------------------------------------------
// Helper: classifica validade da arte em label + tone visual
// ---------------------------------------------------------------------------

function classifyValidity(
  art: CustomArtRead,
  now: Date,
): { label: string; tone: StatusTone } {
  const from = art.valid_from ? new Date(art.valid_from) : null;
  const until = art.valid_until ? new Date(art.valid_until) : null;

  if (until && until.getTime() < now.getTime()) {
    return { label: "Expirada", tone: "destructive" };
  }
  if (from && from.getTime() > now.getTime()) {
    return { label: "Agendada", tone: "muted" };
  }
  if (!from && !until) {
    return { label: "Sem prazo", tone: "warn" };
  }
  if (until) {
    return {
      label: `Vigente até ${until.toLocaleDateString("pt-BR")}`,
      tone: "success",
    };
  }
  return { label: "Vigente", tone: "success" };
}

// ---------------------------------------------------------------------------
// Helper: descrição de escopo loja/área
// ---------------------------------------------------------------------------

function describeScope(
  art: CustomArtRead,
  stores: ReadonlyArray<StoreLite>,
  areas: ReadonlyArray<AreaLite>,
): string {
  const storeName = art.store_id
    ? stores.find((s) => s.id === art.store_id)?.name ?? "loja"
    : null;
  const areaName = art.area_id
    ? areas.find((a) => a.id === art.area_id)?.name ?? "área"
    : null;
  if (storeName && areaName) return `${storeName} • ${areaName}`;
  if (storeName) return storeName;
  if (areaName) return areaName;
  return "";
}

// ---------------------------------------------------------------------------
// Hook: debounce 300ms
// ---------------------------------------------------------------------------

function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebounced(value), delayMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [value, delayMs]);

  return debounced;
}

// ---------------------------------------------------------------------------
// Skeleton de loading
// ---------------------------------------------------------------------------

const GallerySkeleton = memo(function GallerySkeleton() {
  return (
    <div
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4"
      aria-label="Carregando artes…"
      aria-busy="true"
    >
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-card)]"
        >
          <Skeleton className="aspect-video w-full rounded-none" />
          <div className="flex flex-col gap-2 p-3">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="mt-2 h-6 w-20" />
          </div>
        </div>
      ))}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Empty state por aba
// ---------------------------------------------------------------------------

function EmptyState({
  tab,
  hasFilters,
  onUpload,
}: {
  tab: CustomArtTab;
  hasFilters: boolean;
  onUpload: () => void;
}) {
  if (hasFilters) {
    return (
      <div className="rounded-md border border-dashed border-[var(--color-border-strong)] p-8 text-center">
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Nenhuma arte bate com os filtros.
        </p>
      </div>
    );
  }
  if (tab === "Todas") {
    return (
      <div className="rounded-md border border-dashed border-[var(--color-border-strong)] p-10 text-center">
        <ImageIcon
          className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)]"
          aria-hidden="true"
        />
        <h3 className="mt-3 text-base font-medium">Suba sua primeira arte.</h3>
        <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
          Banners, comunicados e promoções entram aqui — as TVs vão exibir
          sozinhas.
        </p>
        <Button onClick={onUpload} className="mt-4">
          <Upload className="h-4 w-4" aria-hidden="true" />
          Subir arte
        </Button>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-dashed border-[var(--color-border-strong)] p-8 text-center">
      <p className="text-sm text-[var(--color-muted-foreground)]">
        {TAB_EMPTY_MESSAGES[tab]}
      </p>
      {tab === "Minhas" && (
        <Button onClick={onUpload} className="mt-4">
          <Upload className="h-4 w-4" aria-hidden="true" />
          Subir arte
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Página principal
// ---------------------------------------------------------------------------

export function CustomArtsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // -- Aba via URL state (TH-55166) ----------------------------------------
  const search = useSearch({ strict: false }) as CustomArtsSearch;
  const activeTab: CustomArtTab = search.tab ?? "Todas";

  const setTab = useCallback(
    (tab: CustomArtTab) => {
      void navigate({

        search: ((prev: Record<string, unknown>) => ({
          ...prev,
          tab: tab === "Todas" ? undefined : tab,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- updater de search do TanStack Router (generics complexos)
        })) as any,
      });
    },
    [navigate],
  );

  // -- Filtros locais --------------------------------------------------------
  const [page, setPage] = useState(1);
  const [storeFilter, setStoreFilter] = useState<string>("");
  const [areaFilter, setAreaFilter] = useState<string>("");
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, 300);

  // Ao trocar de aba, volta pra página 1.
  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  // -- Modais ---------------------------------------------------------------
  const [uploadOpen, setUploadOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [editing, setEditing] = useState<CustomArtRead | null>(null);

  // -- Permissões -----------------------------------------------------------
  const canEditOwn =
    user?.role === "super_admin" ||
    user?.role === "tenant_admin" ||
    user?.role === "store_manager";
  const canDelete = canEditOwn;

  // -- Filtros para o hook --------------------------------------------------
  const filters: CustomArtListFilters = useMemo(
    () => ({
      tab: activeTab,
      search: debouncedSearch || undefined,
      store_id: storeFilter || undefined,
      area_id: areaFilter || undefined,
      page,
      per_page: PER_PAGE,
    }),
    [activeTab, debouncedSearch, storeFilter, areaFilter, page],
  );

  const listQuery = useCustomArtsList(filters);
  const storesQuery = useQuery({
    queryKey: ["stores", "for-custom-arts"],
    queryFn: fetchStoresAll,
    staleTime: 5 * 60_000,
  });
  const areasQuery = useQuery({
    queryKey: ["areas", "for-custom-arts"],
    queryFn: fetchAreasAll,
    staleTime: 5 * 60_000,
  });

  const stores = storesQuery.data?.data ?? [];
  const areas = areasQuery.data?.data ?? [];
  const rows = listQuery.data?.data ?? [];
  const total = listQuery.data?.meta.total ?? 0;
  const now = useMemo(() => new Date(), []);

  const hasFilters = !!storeFilter || !!areaFilter || !!debouncedSearch;

  function resetFilters() {
    setStoreFilter("");
    setAreaFilter("");
    setSearchInput("");
    setPage(1);
  }

  return (
    <RequireAuth>
      <AppLayout
        breadcrumb="OPERAÇÃO / ARTES"
        title="Biblioteca de artes"
        actions={
          // "Nova arte" abre menu com 2 opções: upload manual ou geração via IA
          // (TH-55363). Mantemos o botão único na UI pra não poluir o layout —
          // o menu aparece só ao clicar (Radix focus-trap + ESC).
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button>
                <Plus className="h-4 w-4" aria-hidden="true" />
                Nova arte
                <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setUploadOpen(true)}>
                <Upload className="h-4 w-4" aria-hidden="true" />
                Subir arquivo (PNG/JPG)
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setGenerateOpen(true)}>
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                Gerar via IA
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      >
        <div className="mx-auto max-w-7xl space-y-4">
          {/* Subtítulo + contador */}
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Banners, comunicados e promoções que tocam nas suas telas.
            {typeof total === "number" && rows.length > 0 ? (
              <span className="ml-2 font-mono tabular-nums text-[var(--color-foreground)]">
                {total} {total === 1 ? "arte" : "artes"}
              </span>
            ) : null}
          </p>

          {/* Abas de scope (TH-55166) */}
          <div role="tablist" aria-label="Filtrar por origem">
            <FilterPills
              options={TAB_OPTIONS}
              value={activeTab}
              onChange={setTab}
            />
          </div>

          {/* Barra de filtros */}
          <div className="flex flex-wrap items-end gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-card)] p-3">
            {/* Busca por nome */}
            <div className="grid min-w-[220px] flex-1 gap-1">
              <label
                htmlFor="filter-search"
                className="text-xs uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]"
              >
                Buscar
              </label>
              <Input
                id="filter-search"
                type="search"
                placeholder="Nome da arte…"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setPage(1);
                }}
                aria-label="Buscar arte por nome"
              />
            </div>
            {/* Filtro de loja */}
            <div className="grid gap-1">
              <label
                htmlFor="filter-store"
                className="text-xs uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]"
              >
                Loja
              </label>
              <Select
                id="filter-store"
                value={storeFilter}
                onChange={(e) => {
                  setStoreFilter(e.target.value);
                  setPage(1);
                }}
                className="min-w-[180px]"
              >
                <option value="">Todas</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </div>
            {/* Filtro de área */}
            <div className="grid gap-1">
              <label
                htmlFor="filter-area"
                className="text-xs uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]"
              >
                Área
              </label>
              <Select
                id="filter-area"
                value={areaFilter}
                onChange={(e) => {
                  setAreaFilter(e.target.value);
                  setPage(1);
                }}
                className="min-w-[180px]"
              >
                <option value="">Todas</option>
                {areas.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Select>
            </div>
            {/* Limpar filtros */}
            {hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={resetFilters}
                className="self-end"
              >
                <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                Limpar
              </Button>
            )}
          </div>

          {/* Erro de fetch */}
          {listQuery.error ? (
            <div
              role="alert"
              className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-xs text-[var(--color-destructive)]"
            >
              Erro ao carregar artes.{" "}
              <button
                type="button"
                onClick={() => void listQuery.refetch()}
                className="underline"
              >
                Tentar novamente
              </button>
            </div>
          ) : null}

          {/* Grid de cards */}
          {listQuery.isLoading ? (
            <GallerySkeleton />
          ) : rows.length === 0 ? (
            <EmptyState
              tab={activeTab}
              hasFilters={hasFilters}
              onUpload={() => setUploadOpen(true)}
            />
          ) : (
            <ul
              role="list"
              aria-label={`Artes — aba ${activeTab}`}
              className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4"
            >
              {rows.map((art) => {
                const validity = classifyValidity(art, now);
                const scope = describeScope(art, stores, areas);
                const isOwn = art.origin_label === "Minha";
                return (
                  <li key={art.id}>
                    <CustomArtCard
                      art={art}
                      scopeDescription={scope}
                      validityLabel={validity.label}
                      validityTone={validity.tone}
                      canEdit={isOwn && canEditOwn}
                      onDetails={() => setEditing(art)}
                      onEdit={() => setEditing(art)}
                    />
                  </li>
                );
              })}
            </ul>
          )}

          {/* Paginação */}
          {listQuery.data && total > PER_PAGE ? (
            <div className="mt-4">
              <Pagination
                page={page}
                perPage={PER_PAGE}
                total={total}
                onPageChange={setPage}
              />
            </div>
          ) : null}
        </div>

        {/* Modais */}
        <CustomArtUploadModal
          open={uploadOpen}
          onOpenChange={setUploadOpen}
          stores={stores}
          areas={areas}
        />

        <CustomArtGenerateModal
          open={generateOpen}
          onOpenChange={setGenerateOpen}
          stores={stores}
          areas={areas}
        />

        <CustomArtEditDialog
          art={editing}
          open={editing !== null}
          onOpenChange={(o) => {
            if (!o) setEditing(null);
          }}
          stores={stores}
          areas={areas}
          canDelete={canDelete}
        />
      </AppLayout>
    </RequireAuth>
  );
}
